import React, { useState, useEffect, useRef } from 'react';
import { Shield, AlertCircle, X, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

interface TwoFactorModalProps {
  onSuccess: (userId: string) => void;
  onCancel: () => void;
  accessToken: string;
}

const TwoFactorModal: React.FC<TwoFactorModalProps> = ({ onSuccess, onCancel, accessToken }) => {
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const cancelledRef = useRef(false);

  // Push a history entry so the browser back button triggers sign-out instead
  // of navigating away while a session with no twofa_verified flag is live.
  useEffect(() => {
    window.history.pushState({ twofa: true }, '', window.location.href);

    const handlePopState = () => {
      if (!cancelledRef.current) {
        cancelledRef.current = true;
        handleCancel();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleCancel = async () => {
    cancelledRef.current = true;
    await supabase.auth.signOut();
    onCancel();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (code.length !== 6) {
      setLocalError('Please enter a 6-digit code');
      return;
    }

    setIsLoading(true);
    let verifiedUserId: string | null = null;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twofa-verify`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            Apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ token: code, enable: false }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setLocalError(errorData.error || 'Invalid authentication code. Please try again.');
      } else {
        // Edge function stamped twofa_verified=true in user_metadata via service role.
        // Refresh the session so the client JWT picks up the updated metadata.
        const { data, error } = await supabase.auth.refreshSession();
        if (!error && data.session?.user) {
          verifiedUserId = data.session.user.id;
        } else {
          setLocalError('Session refresh failed. Please try again.');
        }
      }
    } catch {
      setLocalError('Failed to verify code. Please try again.');
    } finally {
      setIsLoading(false);
    }

    if (verifiedUserId) {
      onSuccess(verifiedUserId);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 6) {
      setCode(value);
      setLocalError(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-6 py-5 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-[#40E0D0]/20 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#40E0D0]" />
            </div>
            <div>
              <h2 className="text-white font-semibold text-lg">Two-Factor Authentication</h2>
              <p className="text-gray-400 text-xs">Verify your identity to continue</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
              <svg viewBox="0 0 48 48" className="w-16 h-16">
                <path d="M24 4L8 14L4 24L8 34L24 44L40 34L44 24L40 14L24 4Z" fill="#EA4335"/>
                <path d="M24 4L40 14L44 24L40 34L24 44L24 4Z" fill="#4285F4"/>
                <path d="M4 24L8 14L24 4L24 44L8 34L4 24Z" fill="#34A853"/>
                <path d="M24 44L8 34L4 24L8 14L24 4L24 44Z" fill="#FBBC04"/>
              </svg>
            </div>
            <p className="text-gray-600 text-sm">
              Open your <strong>Google Authenticator</strong> app and enter the 6-digit code for this account.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="2fa-code" className="block text-sm font-medium text-gray-700 mb-2 text-center">
                Authentication Code
              </label>
              <input
                type="text"
                id="2fa-code"
                value={code}
                onChange={handleCodeChange}
                placeholder="000000"
                className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl text-center text-3xl tracking-[0.5em] font-mono focus:ring-2 focus:ring-[#40E0D0] focus:border-[#40E0D0] outline-none transition-colors bg-white text-gray-900 placeholder-gray-300"
                maxLength={6}
                autoComplete="off"
                autoFocus
                inputMode="numeric"
              />
            </div>

            {localError && (
              <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{localError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || code.length !== 6}
              className="w-full bg-[#40E0D0] text-black font-semibold py-3 rounded-xl hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin mr-2 h-5 w-5" />
                  Verifying...
                </>
              ) : (
                'Verify & Sign In'
              )}
            </button>

            <button
              type="button"
              onClick={handleCancel}
              className="w-full bg-gray-100 text-gray-700 font-medium py-3 rounded-xl hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
          </form>

          <div className="mt-5 pt-5 border-t border-gray-100">
            <p className="text-xs text-gray-500 text-center">
              Lost access to your authenticator app?{' '}
              <a href="/request-access" className="text-[#40E0D0] hover:underline font-medium">
                Contact support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorModal;
